import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { StatsCard } from "@/components/dashboard/stats-card";
import { ScheduleCard } from "@/components/dashboard/schedule-card";
import { FitnessCard } from "@/components/dashboard/fitness-card";
import { PlayersCard } from "@/components/dashboard/players-card";
import { MealPlanCard } from "@/components/dashboard/meal-plan-card";
import { PaymentCard } from "@/components/dashboard/payment-card";
import { AnnouncementsCard } from "@/components/dashboard/announcements-card";
import { UserPlus, CalendarCheck2, Users, Heart, DollarSign, Megaphone, Bell } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ScheduleSessionDialog } from "@/components/sessions/schedule-session-dialog";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const currentDate = format(new Date(), "EEEE, MMMM d, yyyy");
  
  const { data: stats, isLoading } = useQuery<any>({
    queryKey: ["/api/dashboard/stats"],
    queryFn: () => api.get("/dashboard/stats")
  });
  
  return (
    <div className="space-y-4 md:space-y-6 w-full">
      {/* Dashboard Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 heading" data-testid="legacy-dashboard-section-heading">Dashboard</h1>
          <p className="text-sm md:text-base text-gray-600">{currentDate}</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Link to="/players/add" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto flex items-center justify-center" data-testid="legacy-dashboard-add-player-button">
              <UserPlus className="h-4 w-4 mr-1" />
              <span>Add New Player</span>
            </Button>
          </Link>
          <ScheduleSessionDialog testId="legacy-dashboard-schedule-session-button" />
        </div>
      </div>
      
      {/* Stats Overview - 1 col mobile, 2 cols tablet, 4 cols desktop */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div data-testid="legacy-dashboard-card-registered-players">
          <StatsCard 
            title="Registered Players"
            value={isLoading ? "..." : stats?.playerCount || 0}
            icon={<Users />}
            iconBgColor="bg-primary/10"
            iconColor="text-primary"
            changeText="+4 since last month"
            changeValue={4}
            changeColor="text-secondary"
          />
        </div>
        
        <div data-testid="legacy-dashboard-card-upcoming-sessions">
          <StatsCard 
            title="Upcoming Sessions"
            value={isLoading ? "..." : stats?.sessionCount || 0}
            icon={<CalendarCheck2 />}
            iconBgColor="bg-secondary/10"
            iconColor="text-secondary"
            changeText="Next: Today at 4:00 PM"
          />
        </div>
        
        <div data-testid="legacy-dashboard-card-pending-payments">
          <StatsCard 
            title="Pending Payments"
            value={isLoading ? "..." : `${Number(stats?.pendingPaymentsTotal || 0).toFixed(2)}`}
            icon={<DollarSign />}
            iconBgColor="bg-danger/10"
            iconColor="text-danger"
            changeText={`${stats?.pendingPaymentsCount || 0} players with pending fees`}
            changeValue={stats?.pendingPaymentsCount || 0}
            changeColor="text-danger"
          />
        </div>
        
        <div data-testid="legacy-dashboard-card-recent-announcements">
          <StatsCard 
            title="Recent Announcements"
            value={isLoading ? "..." : stats?.announcementCount || 0}
            icon={<Megaphone />}
            iconBgColor="bg-accent/10"
            iconColor="text-accent"
            changeText={stats?.lastAnnouncementDate ? `Last sent: ${formatDistanceToNow(new Date(stats.lastAnnouncementDate), { addSuffix: true })}` : "No recent announcements"}
          />
        </div>
      </div>
      
      {/* Main Dashboard Grid - Stack on mobile, 3 columns on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Column 1: Today's Schedule & Fitness Progress */}
        <div className="space-y-4 md:space-y-6">
          <ScheduleCard />
          <FitnessCard />
        </div>
        
        {/* Column 2: Players List & Meal Plan */}
        <div className="space-y-4 md:space-y-6">
          <PlayersCard />
          <MealPlanCard />
        </div>
        
        {/* Column 3: Upcoming Payments & Announcements */}
        <div className="space-y-4 md:space-y-6">
          <PaymentCard />
          <AnnouncementsCard />
        </div>
      </div>
    </div>
  );
  
  function formatDistanceToNow(date: Date, options: { addSuffix: boolean }): string {
    const now = new Date();
    const diffDays = Math.round(Math.abs((now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000)));
    
    if (diffDays === 0) return "today";
    if (diffDays === 1) return "yesterday";
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.round(diffDays / 7)} weeks ago`;
    return `${Math.round(diffDays / 30)} months ago`;
  }
}
